import React, { useEffect, useState } from 'react';
import { Mail, Image } from 'lucide-react';
import { api } from '../../lib/api';
import type { SiteSettings } from '../../types';
import { useUpload } from '../../lib/useUpload';
import { Spinner, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from '../../components/ui';

type HeroKey = 'hero_video_home' | 'hero_video_about' | 'hero_image_contact' | 'hero_image_management' | 'hero_image_properties';
type HeroKind = 'video' | 'image';
type BrandKey = 'logo_url' | 'favicon_url';

const BRAND_FIELDS: { key: BrandKey; label: string; hint: string }[] = [
  { key: 'logo_url', label: 'Website Logo', hint: 'PNG/SVG/JPEG shown in the header and footer. Transparent background works best.' },
  { key: 'favicon_url', label: 'Browser Icon (Favicon)', hint: 'Small square PNG (e.g. 64x64) shown in the browser tab.' },
];

const HERO_PAGES: { key: HeroKey; label: string; kind: HeroKind }[] = [
  { key: 'hero_video_home', label: 'Home Page', kind: 'video' },
  { key: 'hero_video_about', label: 'About Page', kind: 'video' },
  { key: 'hero_image_contact', label: 'Contact Page', kind: 'image' },
  { key: 'hero_image_management', label: 'Management Page', kind: 'image' },
  { key: 'hero_image_properties', label: 'Sales & Leasing (Property Search)', kind: 'image' },
];

const HERO_ACCEPT: Record<HeroKind, string> = {
  video: 'video/mp4,video/quicktime,.mp4,.mov',
  image: 'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp',
};

export default function Appearance() {
  const [heroes, setHeroes] = useState<Record<string, string>>({});
  const [brand, setBrand] = useState<Record<BrandKey, string>>({ logo_url: '', favicon_url: '' });
  const [resendApiKey, setResendApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  // One progress bar per upload group: a hero file and a brand image can never
  // be in flight at the same time, and sharing a single hook would make them
  // fight over the same state.
  const heroUpload = useUpload();
  const brandUpload = useUpload();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fillFromSettings = (s: SiteSettings) => {
    const v: Record<string, string> = {};
    HERO_PAGES.forEach(({ key }) => { v[key] = (s as Record<string, string>)[key] || ''; });
    setHeroes(v);
    setResendApiKey((s as { resend_api_key?: string }).resend_api_key || '');
    setBrand({
      logo_url: (s as Record<string, string>).logo_url || '',
      favicon_url: (s as Record<string, string>).favicon_url || '',
    });
  };

  useEffect(() => {
    api
      .get<SiteSettings>('/settings/private')
      .then(fillFromSettings)
      .catch(() => {
        api.get<SiteSettings>('/settings').then(fillFromSettings).catch(() => {});
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await api.put('/settings', heroes);
      setMessage('Hero background settings saved.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await api.put('/settings', { resend_api_key: resendApiKey });
      setMessage('Resend API key saved. Password recovery emails will use this key.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadHero = async (page: { key: HeroKey; label: string; kind: HeroKind }, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(page.key);
    heroUpload.reset();
    setMessage('');
    setError('');
    try {
      const fd = new FormData();
      fd.append(page.kind === 'video' ? 'video' : 'image', file);
      fd.append('key', page.key);
      const endpoint = page.kind === 'video' ? '/settings/hero-video' : '/settings/hero-image';
      const result = await heroUpload.run<Record<string, string>>(endpoint, fd);
      setHeroes((h) => ({ ...h, [page.key]: result[page.key] }));
      setMessage(`${page.label} hero ${page.kind} updated.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploadingKey(null);
      e.target.value = '';
    }
  };

  const handleUploadBrand = async (key: BrandKey, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(key);
    brandUpload.reset();
    setMessage('');
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('key', key);
      const result = await brandUpload.run<Record<string, string>>('/settings/media', fd);
      setBrand((b) => ({ ...b, [key]: result[key] }));
      const field = BRAND_FIELDS.find((f) => f.key === key);
      setMessage(`${field?.label || 'Brand'} image updated.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploadingKey(null);
      e.target.value = '';
    }
  };

  if (loading) return <Spinner label="Loading settings..." />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Appearance & Email</h1>
      {message && <div className="px-4 py-3 rounded-lg bg-green-50 text-green-800 text-sm">{message}</div>}
      {error && <Alert>{error}</Alert>}

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <h2 className="font-semibold mb-1">Hero Backgrounds</h2>
        <p className="text-sm text-gray-500 mb-4">
          Each page hero is editable. Home and About use a video background (MP4 or MOV). Contact, Management and Sales
          &amp; Leasing use an image background (JPG, PNG, GIF or WebP). Paste a URL or upload a file for each page.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {HERO_PAGES.map((page) => (
            <div key={page.key} className="border border-gray-100 rounded-lg p-4">
              <h3 className="font-medium text-sm mb-3">{page.label}</h3>
              <div className="grid sm:grid-cols-2 gap-3 items-end">
                <div>
                  <label className={labelClass}>{page.kind === 'video' ? 'Video URL (MP4/MOV)' : 'Image URL (JPG/PNG/WebP)'}</label>
                  <input
                    className={inputClass}
                    value={heroes[page.key] || ''}
                    onChange={(e) => setHeroes((h) => ({ ...h, [page.key]: e.target.value }))}
                    placeholder={page.kind === 'video' ? 'https://.../hero.mp4' : 'https://.../hero.jpg'}
                  />
                </div>
                <div>
                  <label className={labelClass}>Upload a {page.kind} file</label>
                  <input
                    type="file"
                    accept={HERO_ACCEPT[page.kind]}
                    className={inputClass}
                    onChange={(e) => handleUploadHero(page, e)}
                    disabled={uploadingKey !== null}
                  />
                </div>
              </div>
              {heroes[page.key] && (
                page.kind === 'video' ? (
                  <video className="w-full rounded-lg mt-3" controls muted loop src={heroes[page.key]} />
                ) : (
                  <img src={heroes[page.key]} alt={`${page.label} hero preview`} className="w-full max-h-56 object-cover rounded-lg mt-3" />
                )
              )}
              {uploadingKey === page.key && (
                <UploadProgress
                  percent={heroUpload.percent}
                  phase={heroUpload.phase}
                  error={heroUpload.error}
                  onCancel={heroUpload.cancel}
                />
              )}
            </div>
          ))}

          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'Saving...' : 'Save All Hero Backgrounds'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <div className="flex items-center mb-1">
          <Image className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="font-semibold">Logo & Browser Icon</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Upload the brand logo used across the website and the small icon shown in the browser tab.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {BRAND_FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="border border-gray-100 rounded-lg p-4">
              <h3 className="font-medium text-sm mb-1">{label}</h3>
              <p className="text-xs text-gray-500 mb-3">{hint}</p>
              {brand[key] ? (
                key === 'logo_url'
                  ? (
                    <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg mb-3 overflow-hidden">
                      <img src={brand[key]} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                    </div>
                  )
                  : (
                    <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg mb-3">
                      <img src={brand[key]} alt="Favicon preview" className="h-10 w-10 object-contain" />
                    </div>
                  )
              ) : (
                <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg mb-3 text-xs text-gray-400">No image set</div>
              )}
              <input
                type="file"
                accept="image/*"
                className={inputClass}
                onChange={(e) => handleUploadBrand(key, e)}
                disabled={uploadingKey !== null}
              />
              {uploadingKey === key && (
                <UploadProgress
                  percent={brandUpload.percent}
                  phase={brandUpload.phase}
                  error={brandUpload.error}
                  onCancel={brandUpload.cancel}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <div className="flex items-center mb-1">
          <Mail className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="font-semibold">Password Recovery Email (Resend)</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          This API key is used to send password reset emails. It is stored on the server and controlled from this portal.
        </p>
        <form onSubmit={handleSaveResend} className="space-y-4">
          <div>
            <label className={labelClass}>Resend API Key</label>
            <input
              type="password"
              className={inputClass}
              value={resendApiKey}
              onChange={(e) => setResendApiKey(e.target.value)}
              placeholder="re_..."
              autoComplete="off"
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={secondaryBtn}>
              {saving ? 'Saving...' : 'Save Resend Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
